import { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Input, Popconfirm, Tooltip, message as toast } from 'antd';
import { DeleteOutlined, PushpinFilled, PushpinOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { ClassPill, PersonAvatar, Pill } from 'pages/support-shared/components';
import { SupportNote, ThreadDetail } from 'pages/support-shared/types';
import { chatTime, supportError } from 'pages/support-shared/utils';
import { formatNumber } from 'pages/star-shared/utils';
import { colors } from 'theme/tokens';
import styles from './styles.module.scss';

const ATTENDANCE_LABEL: Record<string, string> = {
  normal: 'Đúng giờ',
  late: 'Đi trễ',
  early: 'Về sớm',
  'late-early': 'Trễ & về sớm',
  absent: 'Vắng',
  'no-checkout': 'Chưa chấm ra',
};

interface Props {
  detail: ThreadDetail | null;
  meId?: number;
  isAdmin: boolean;
}

const time = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }) : '—';

const ProfilePanel = ({ detail, meId, isAdmin }: Props) => {
  const profile = detail?.profile;
  const userId = profile?.id;
  const [notes, setNotes] = useState<SupportNote[]>([]);
  const [draft, setDraft] = useState('');
  const [pin, setPin] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadNotes = useCallback(() => {
    if (!userId) return;
    AxiosUtils.get(API.supportNotes, { userId })
      .then((res: any) => setNotes(res.data || []))
      .catch(() => undefined);
  }, [userId]);

  useEffect(() => {
    setNotes([]);
    setDraft('');
    loadNotes();
  }, [loadNotes]);

  const addNote = async () => {
    const content = draft.trim();
    if (!content || !userId) return;
    setSaving(true);
    try {
      const res: any = await AxiosUtils.post(API.supportNotes, {
        userId,
        threadId: detail?.thread.id,
        content,
        pinned: pin,
      });
      setNotes(res.data || []);
      setDraft('');
      setPin(false);
    } catch (error) {
      toast.error(supportError(error));
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (note: SupportNote) => {
    try {
      const res: any = await AxiosUtils.patch(`${API.supportNotes}/${note.id}`, { pinned: !note.pinned });
      setNotes(res.data || []);
    } catch (error) {
      toast.error(supportError(error));
    }
  };

  const removeNote = async (note: SupportNote) => {
    try {
      const res: any = await AxiosUtils._delete(`${API.supportNotes}/${note.id}`, undefined);
      setNotes(res.data || []);
    } catch (error) {
      toast.error(supportError(error));
    }
  };

  if (!profile) {
    return <div className={`${styles.column} ${styles.profile}`} />;
  }

  const attendance = profile.attendanceToday || [];
  const pending = profile.pendingRequests;

  return (
    <aside className={`${styles.column} ${styles.profile}`} aria-label="Hồ sơ người dùng">
      <div className={styles.profileCard}>
        <PersonAvatar name={profile.name} avatar={profile.avatar} size={64} />
        <h3>{profile.name}</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
          <ClassPill value={profile.userClass} label={profile.userClassLabel} />
          {profile.departmentLabel && (
            <Pill color={colors.textSecondary} bg={colors.surfaceMuted}>
              {profile.departmentLabel}
            </Pill>
          )}
          {profile.isAccountDisabled && (
            <Pill color={colors.dangerInk} bg={colors.dangerSoft}>
              Đang bị khoá
            </Pill>
          )}
          {profile.deleted && (
            <Pill color={colors.dangerInk} bg={colors.dangerSoft}>
              Đã xoá tài khoản
            </Pill>
          )}
        </div>
      </div>

      <dl className={styles.facts}>
        <dt>ID</dt>
        <dd>#{profile.id}</dd>
        {profile.username && (
          <>
            <dt>Username</dt>
            <dd>@{profile.username}</dd>
          </>
        )}
        {profile.fullName && (
          <>
            <dt>Họ tên</dt>
            <dd>{profile.fullName}</dd>
          </>
        )}
        <dt>UID TikTok</dt>
        <dd>{profile.tiktokUid || '—'}</dd>
        {profile.familyName && (
          <>
            <dt>Nhóm quản lý</dt>
            <dd>{profile.familyName}</dd>
          </>
        )}
        <dt>Điểm Sao</dt>
        <dd>{profile.stars === null ? '—' : `${formatNumber(profile.stars)} ⭐`}</dd>
        {isAdmin && (
          <>
            <dt>Email</dt>
            <dd>{profile.email || '—'}</dd>
            <dt>SĐT</dt>
            <dd>{profile.phone || '—'}</dd>
          </>
        )}
        <dt>Tham gia</dt>
        <dd>{profile.joinedAt ? new Date(profile.joinedAt).toLocaleDateString('vi-VN') : '—'}</dd>
      </dl>

      {(profile.userClass === 'STAFF' || attendance.length > 0) && (
        <div className={styles.section}>
          <h4>Chấm công hôm nay</h4>
          {attendance.length ? (
            attendance.map((a, i) => (
              <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
                {a.shiftName || 'Ca'}: <strong>{ATTENDANCE_LABEL[a.status] || a.status}</strong> · vào {time(a.checkinTime)} · ra{' '}
                {time(a.checkoutTime)}
              </div>
            ))
          ) : (
            <div style={{ fontSize: 13, color: colors.textMuted }}>Chưa có bản ghi.</div>
          )}
          {pending && (pending.leave > 0 || pending.overtime > 0) && (
            <div style={{ fontSize: 13, marginTop: 6, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {pending.leave > 0 && (
                <Link to="/admin/attendance/leave-requests?status=PENDING">{pending.leave} đơn nghỉ chờ duyệt</Link>
              )}
              {pending.overtime > 0 && <Link to="/overtime-management?status=PENDING">{pending.overtime} đơn tăng ca chờ duyệt</Link>}
            </div>
          )}
        </div>
      )}

      <div className={styles.section}>
        <h4>Ghi chú nội bộ</h4>
        <Input.TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoSize={{ minRows: 2, maxRows: 6 }}
          maxLength={2000}
          placeholder="Chỉ nhân viên thấy — vd. hay quên chấm công ra ca, đã hẹn gọi lại thứ Hai…"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) addNote();
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '6px 0 10px' }}>
          <Checkbox checked={pin} onChange={(e) => setPin(e.target.checked)}>
            Ghim
          </Checkbox>
          <Button size="small" type="primary" onClick={addNote} loading={saving} disabled={!draft.trim()}>
            Lưu ghi chú
          </Button>
        </div>
        {notes.map((note) => (
          <div key={note.id} className={`${styles.note} ${note.pinned ? styles.notePinned : ''}`}>
            {note.content}
            <div className={styles.noteMeta}>
              <span>
                {note.author.name} · {chatTime(note.createdAt)}
              </span>
              <span>
                <Tooltip title={note.pinned ? 'Bỏ ghim' : 'Ghim lên đầu'}>
                  <Button
                    type="text"
                    size="small"
                    icon={note.pinned ? <PushpinFilled style={{ color: colors.starInk }} /> : <PushpinOutlined />}
                    onClick={() => togglePin(note)}
                    aria-label={note.pinned ? 'Bỏ ghim' : 'Ghim'}
                  />
                </Tooltip>
                {(note.author.id === meId || isAdmin) && (
                  <Popconfirm title="Xoá ghi chú này?" okText="Xoá" cancelText="Không" onConfirm={() => removeNote(note)}>
                    <Button type="text" size="small" icon={<DeleteOutlined />} aria-label="Xoá ghi chú" />
                  </Popconfirm>
                )}
              </span>
            </div>
          </div>
        ))}
        {!notes.length && <div style={{ fontSize: 13, color: colors.textMuted }}>Chưa có ghi chú.</div>}
      </div>
    </aside>
  );
};

export default ProfilePanel;
